import type * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { EVENT } from "../core/keys"
import type { Character, Player, RunStage, Upgrade } from "../types"

export interface RunProgressSnapshot {
  player: Player
  score: number
  coins: number
  currentWave: number
  runDistance: number
}

/**
 * Singleton that wraps Phaser's global registry for typed, centralised
 * access to run-session state. Call `GameState.init(game)` once after the
 * Phaser.Game instance is created.
 */
export class GameState {
  private static _instance: GameState | null = null

  private registry: Phaser.Data.DataManager

  private constructor(game: Phaser.Game) {
    this.registry = game.registry
    this.reset()
  }

  // ── Lifecycle ────────────────────────────────────────────────

  static init(game: Phaser.Game): GameState {
    GameState._instance = new GameState(game)
    return GameState._instance
  }

  static get instance(): GameState {
    if (!GameState._instance) {
      throw new Error(
        "GameState has not been initialised. Call GameState.init(game) first.",
      )
    }
    return GameState._instance
  }

  /** Reset all session state to defaults (e.g. on new run). */
  reset(): void {
    this.registry.set("character", null)
    this.registry.set("score", 0)
    this.registry.set("coins", 0)
    this.registry.set("currentWave", 1)
    this.registry.set("runDistance", 0)
    this.registry.set("runState", "menu" as RunStage)
    this.registry.set("player", null)
    this.registry.set("upgradeHistory", [])
    this.registry.set("waveTimer", 0)
    this.registry.set("playerName", "")
    this.registry.set("playerColor", "")
  }

  // ── Portal Support ───────────────────────────────────────────

  get playerName(): string {
    return (this.registry.get("playerName") as string) ?? ""
  }

  set playerName(value: string) {
    this.registry.set("playerName", value)
  }

  get playerColor(): string {
    return (this.registry.get("playerColor") as string) ?? ""
  }

  set playerColor(value: string) {
    this.registry.set("playerColor", value)
  }

  // ── Character ────────────────────────────────────────────────

  get character(): Character | null {
    return this.registry.get("character") as Character | null
  }

  set character(value: Character | null) {
    this.registry.set("character", value)
  }

  // ── Score ────────────────────────────────────────────────────

  get score(): number {
    return (this.registry.get("score") as number) ?? 0
  }

  set score(value: number) {
    this.registry.set("score", value)
  }

  // ── Coins ────────────────────────────────────────────────────

  get coins(): number {
    return (this.registry.get("coins") as number) ?? 0
  }

  set coins(value: number) {
    this.registry.set("coins", value)
  }

  // ── Current wave ─────────────────────────────────────────────

  get currentWave(): number {
    return (this.registry.get("currentWave") as number) ?? 1
  }

  set currentWave(value: number) {
    this.registry.set("currentWave", value)
  }

  // ── Run distance ─────────────────────────────────────────────

  get runDistance(): number {
    return (this.registry.get("runDistance") as number) ?? 0
  }

  set runDistance(value: number) {
    this.registry.set("runDistance", value)
  }

  // ── Run state ────────────────────────────────────────────────

  get runState(): RunStage {
    return (this.registry.get("runState") as RunStage) ?? "menu"
  }

  set runState(value: RunStage) {
    if (this.runState === value) return
    this.registry.set("runState", value)
    EventBus.emit(EVENT.RUN_STATE_CHANGED, value)
  }

  // ── Player ───────────────────────────────────────────────────

  get player(): Player | null {
    return this.registry.get("player") as Player | null
  }

  set player(value: Player | null) {
    this.registry.set("player", value)
  }

  // ── Upgrade history ──────────────────────────────────────────

  get upgradeHistory(): Upgrade[] {
    return (this.registry.get("upgradeHistory") as Upgrade[]) ?? []
  }

  set upgradeHistory(value: Upgrade[]) {
    this.registry.set("upgradeHistory", value)
  }

  // ── Wave timer ───────────────────────────────────────────────

  get waveTimer(): number {
    return (this.registry.get("waveTimer") as number) ?? 0
  }

  set waveTimer(value: number) {
    this.registry.set("waveTimer", value)
  }

  // ── Run progress handoff ─────────────────────────────────────

  captureRunProgress(snapshot: RunProgressSnapshot): void {
    this.player = snapshot.player
    this.score = snapshot.score
    this.coins = snapshot.coins
    this.currentWave = snapshot.currentWave
    this.runDistance = snapshot.runDistance
  }

  restoreRunProgress(): RunProgressSnapshot | null {
    const player = this.player
    if (!player) return null

    return {
      player,
      score: this.score,
      coins: this.coins,
      currentWave: this.currentWave,
      runDistance: this.runDistance,
    }
  }
}
